import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  formatDateInputBr,
  formatRainMm,
  parseDateInputLocal,
} from "../../utils/rainHeatmapUtils";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function formatChartTick(dateStr, totalDays) {
  const date = parseDateInputLocal(dateStr);
  if (!date) return "";
  if (totalDays <= 40) {
    return `${date.getDate()}/${date.getMonth() + 1}`;
  }
  if (date.getDate() === 1) {
    return date.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
  }
  return "";
}

function DayAxisTick({ x, y, payload, mmByDate, compact }) {
  const mm = mmByDate[payload.value];
  const hasRain = Number(mm) > 0;
  const date = parseDateInputLocal(payload.value);
  const top = date ? `${date.getDate()}/${date.getMonth() + 1}` : payload.value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={11} textAnchor="middle" fontSize={compact ? 8 : 10} fill="#607d8b">
        {top}
      </text>
      <text
        dy={23}
        textAnchor="middle"
        fontSize={compact ? 8 : 10}
        fontWeight={hasRain ? 700 : 500}
        fill={hasRain ? "#1565C0" : "#90A4AE"}
      >
        {formatRainMm(mm)}
      </text>
    </g>
  );
}

function RainMonthTooltip({ active, payload, locale }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const date = parseDateInputLocal(row.date);
  const title = date
    ? date.toLocaleDateString(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : formatDateInputBr(row.date);
  return (
    <div className="rain-month-chart__tooltip">
      <strong>{title}</strong>
      <span>{formatRainMm(row.mm)} mm</span>
    </div>
  );
}

function RainMonthChart({
  monthId,
  monthTitle,
  series,
  isExample = false,
  windowStart,
  windowEnd,
  onDayClick,
  onWindowChange,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en-US" ? "en-US" : "pt-BR";
  const clickLockRef = useRef(false);
  const skipBrushRef = useRef(true);
  const plotRef = useRef(null);
  const data = series || [];
  const lastIndex = Math.max(data.length - 1, 0);
  const mmByDate = {};
  data.forEach((row) => {
    mmByDate[row.date] = row.mm;
  });
  const [brushRange, setBrushRange] = useState({
    startIndex: 0,
    endIndex: lastIndex,
  });

  useEffect(() => {
    skipBrushRef.current = true;
    setBrushRange({
      startIndex: 0,
      endIndex: Math.max((series || []).length - 1, 0),
    });
  }, [monthId, lastIndex]);

  const brushStart = clamp(brushRange.startIndex, 0, lastIndex);
  const brushEnd = clamp(Math.max(brushRange.endIndex, brushStart), 0, lastIndex);

  const showAllDayTicks = data.length <= 31;
  const tickInterval = showAllDayTicks
    ? 0
    : Math.max(0, Math.ceil(data.length / 12) - 1);
  const barSize = data.length > 180 ? 4 : data.length > 60 ? 8 : 22;

  useEffect(() => {
    const node = plotRef.current;
    if (!node || lastIndex === 0) return undefined;

    function handleWheel(event) {
      event.preventDefault();
      const span = brushEnd - brushStart;
      if (span <= 0 || span >= lastIndex) return;
      const step = (event.deltaY > 0 ? 1 : -1) * Math.max(1, Math.round(span / 12));
      const maxStart = lastIndex - span;
      const nextStart = clamp(brushStart + step, 0, maxStart);
      if (nextStart === brushStart) return;
      skipBrushRef.current = true;
      setBrushRange({
        startIndex: nextStart,
        endIndex: nextStart + span,
      });
    }

    node.addEventListener("wheel", handleWheel, { passive: false });
    return () => node.removeEventListener("wheel", handleWheel);
  }, [brushStart, brushEnd, lastIndex]);

  function lockClicks() {
    clickLockRef.current = true;
    window.setTimeout(() => {
      clickLockRef.current = false;
    }, 250);
  }

  function handleBarClick(state) {
    const payload =
      state && state.activePayload && state.activePayload[0]
        ? state.activePayload[0].payload
        : null;
    if (!payload || !payload.date || !onDayClick) return;
    lockClicks();
    skipBrushRef.current = true;
    onDayClick(payload.date);
  }

  function handleBrushChange(range) {
    if (skipBrushRef.current) {
      skipBrushRef.current = false;
      return;
    }
    if (clickLockRef.current || !range || !onWindowChange) return;
    const start = data[range.startIndex];
    const end = data[range.endIndex];
    if (!start || !end) return;
    setBrushRange({
      startIndex: range.startIndex,
      endIndex: range.endIndex,
    });
    if (start.date === windowStart && end.date === windowEnd) return;
    onWindowChange(start.date, end.date);
  }

  return (
    <section
      className={
        isExample ? "rain-month-chart rain-month-chart--example" : "rain-month-chart"
      }
      aria-label={t("Chuva")}
    >
      <div className="rain-month-chart__head">
        <div>
          <strong>{t("Chuva do mês")}</strong>
          <span>{monthTitle}</span>
        </div>
      </div>
      <p className="rain-month-chart__hint">
        {isExample
          ? t(
              "Exemplo visual: {{count}} dias de ponta a ponta, de hoje para trás. Os mm reais aparecem quando houver lançamento.",
              { count: data.length }
            )
          : t(
              "Clique na barra para abrir o dia. A régua embaixo só muda se você arrastar ou usar a bolinha do mouse."
            )}
      </p>
      <div className="rain-month-chart__plot" ref={plotRef}>
        <ResponsiveContainer width="100%" height={248}>
          <BarChart
            data={data}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
            onClick={handleBarClick}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              interval={tickInterval}
              height={36}
              minTickGap={showAllDayTicks ? 0 : 8}
              tick={
                <DayAxisTick mmByDate={mmByDate} compact={showAllDayTicks} />
              }
            />
            <YAxis
              width={36}
              tick={{ fontSize: 11, fill: "#607d8b" }}
              tickFormatter={(value) => formatRainMm(value)}
            />
            <Tooltip
              cursor={{ fill: "rgba(21, 101, 192, 0.08)" }}
              content={<RainMonthTooltip locale={locale} />}
            />
            <Bar
              dataKey="mm"
              name="mm"
              maxBarSize={barSize}
              radius={[3, 3, 0, 0]}
            >
              {data.map((row) => {
                const selected =
                  row.date >= windowStart && row.date <= windowEnd;
                return (
                  <Cell
                    key={row.date}
                    fill={
                      selected ? "#1565C0" : row.mm > 0 ? "#64B5F6" : "#CFD8DC"
                    }
                    cursor="pointer"
                  />
                );
              })}
            </Bar>
            {data.length > 1 ? (
              <Brush
                dataKey="date"
                height={26}
                startIndex={brushStart}
                endIndex={brushEnd}
                stroke="#1565C0"
                fill="#E3F2FD"
                travellerWidth={10}
                tickFormatter={(value) => formatChartTick(value, 30)}
                onChange={handleBrushChange}
              />
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export default RainMonthChart;
